import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errText, media } from "../../lib/api";
import { ZoomImage } from "../../components/ZoomImage";
import { Badge, DataTable, KeyRow, OrderLink, Panel, Stat, TabBar, money, shortDate, stamp, statusTone, useTable } from "./ui";
import OrderDrawer from "./OrderDrawer";

const Money = ({ v }) => <span className="tabular whitespace-nowrap">{money(v)} MAD</span>;

// Admin-only panel to grant / revoke multi-city selling for a shop. Florists
// cannot flip this themselves — Golden Flower is the flagship example.
const MultiCityPanel = ({ florist, onSaved }) => {
  const [cities, setCities] = useState(null);
  const [enabled, setEnabled] = useState(!!florist.multi_city_enabled);
  const [selected, setSelected] = useState(() =>
    new Set((florist.serves_cities || []).map((c) => c.id)));
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/cities").then((r) => setCities((r.data.cities || []).filter((c) => c.is_active !== false)))
      .catch(() => setCities([]));
  }, []);
  useEffect(() => {
    setEnabled(!!florist.multi_city_enabled);
    setSelected(new Set((florist.serves_cities || []).map((c) => c.id)));
  }, [florist.multi_city_enabled, florist.serves_cities]);
  const others = useMemo(() =>
    (cities || []).filter((c) => c.id !== florist.city_id), [cities, florist.city_id]);
  const toggleCity = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const save = async () => {
    setMsg(""); setBusy(true);
    try {
      const { data } = await api.post(`/admin/florists/${florist.id}/multi-city`,
                                       { enabled, city_ids: Array.from(selected) });
      setMsg(data.multi_city_enabled
        ? `Multi-city ON — selling in ${data.serves_cities.length + 1} cities.`
        : "Multi-city OFF — shop restricted to home city.");
      onSaved?.();
    } catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Multi-city selling" testid="florist-multi-city">
      <p className="text-xs text-muted">
        Home city is <b>{florist.city}</b>. Tick the extra cities this shop is allowed to sell
        into. Only admins can flip this — the florist sees no toggle.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="florist-multi-city-toggle"
               checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Allow this shop to sell in more than one city</span>
      </label>
      {enabled ? (
        cities === null ? (
          <p className="text-xs text-muted">Loading cities…</p>
        ) : (
          <div className="grid grid-cols-2 gap-1 mt-1" data-testid="florist-multi-city-cities">
            {others.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={selected.has(c.id)}
                       data-testid={`florist-multi-city-city-${c.id}`}
                       onChange={() => toggleCity(c.id)} />
                <span>{c.name?.en || c.name || c.key}</span>
              </label>
            ))}
            {others.length === 0 ? (
              <p className="text-xs text-muted col-span-2">Only one active city on the platform — nothing to add.</p>
            ) : null}
          </div>
        )
      ) : null}
      <div className="flex items-center gap-2">
        <button className="btn-primary text-xs disabled:opacity-50" data-testid="florist-multi-city-save"
                disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        {msg ? <span className="text-[11px] text-brass break-words" data-testid="florist-multi-city-msg">{msg}</span> : null}
      </div>
    </Panel>
  );
};

const OrdersTab = ({ floristId }) => {
  const table = useTable("/admin/tables/orders", { florist_id: floristId });
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("order");
  const close = () => { const n = Object.fromEntries(sp.entries()); delete n.order; setSp(n, { replace: true }); };
  const columns = [
    { key: "order_number", label: "Order", render: (r) => <OrderLink number={r.order_number} id={r.id} /> },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { key: "delivery_date", label: "Delivery", sortable: true, render: (r) => shortDate(r.delivery_date) },
    { key: "recipient_name", label: "Recipient" },
    { key: "total_mad", label: "Total", sortable: true, render: (r) => <Money v={r.total_mad} /> },
    { key: "gateway_fee_mad", label: "Card fee", render: (r) => <Money v={r.gateway_fee_mad} /> },
    { key: "payout_mad", label: "Payout", sortable: true, render: (r) => <Money v={r.payout_mad} /> },
  ];
  return (
    <>
      <DataTable table={table} columns={columns} testid="florist-orders-table" name="shop-orders" searchPlaceholder="Order or recipient…" />
      {openId ? <OrderDrawer orderId={openId} onClose={close} onChanged={table.reload} /> : null}
    </>
  );
};

const CatalogueTab = ({ floristId }) => {
  const table = useTable("/admin/tables/products", { florist_id: floristId });
  const columns = [
    { key: "title", label: "Bouquet", render: (r) => (
      <span className="flex items-center gap-2">
        {r.image ? <ZoomImage src={media(r.image)} alt={r.title} caption={r.title}
                              className="w-8 aspect-[4/5] shrink-0 inline-block align-middle me-2"
                              testid={`partner-catalogue-photo-${r.id}`} /> : null}{r.title}
      </span>
    ) },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { key: "base_price_eur", label: "Selling EUR", sortable: true, render: (r) => <span data-testid={`partner-admin-retail-${r.id}`}>€{Number(r.base_price_eur || 0).toFixed(2)}</span> },
    { key: "sale_percent", label: "Sale", render: (r) => (r.sale_percent ? `−${r.sale_percent}%` : "—") },
    { key: "sales_count", label: "Sold", sortable: true, render: (r) => <span className="tabular">{r.sales_count}</span> },
    { key: "rating", label: "Rating", sortable: true, render: (r) => <span className="tabular">{r.rating ?? "—"}</span> },
    { key: "seo_complete", label: "SEO", render: (r) => <Badge tone={r.seo_complete ? "good" : "warn"}>{r.seo_complete ? "written" : "missing"}</Badge> },
  ];
  return <DataTable table={table} columns={columns} testid="florist-catalogue-table" name="shop-catalogue" searchPlaceholder="Bouquet…" />;
};

const PayoutsTab = ({ floristId }) => {
  const table = useTable("/admin/tables/payouts", { florist_id: floristId });
  const statement = async (id) => {
    const r = await api.get(`/admin/payouts/${id}/statement`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "text/html" })), "_blank");
  };
  const columns = [
    { key: "created_at", label: "Created", sortable: true, render: (r) => stamp(r.created_at) },
    { key: "period", label: "Period", render: (r) => `${shortDate(r.period_start)} → ${shortDate(r.period_end)}` },
    { key: "orders", label: "Orders", render: (r) => <span className="tabular">{r.orders}</span> },
    { key: "gross_mad", label: "Gross", render: (r) => <Money v={r.gross_mad} /> },
    { key: "gateway_fees_mad", label: "Card fees", render: (r) => <Money v={r.gateway_fees_mad} /> },
    { key: "net_mad", label: "Net", sortable: true, render: (r) => <Money v={r.net_mad} /> },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <button className="btn-ghost text-[11px]" data-testid={`florist-statement-${r.id}`} onClick={() => statement(r.id)}>Statement</button>
    ) },
  ];
  return <DataTable table={table} columns={columns} testid="florist-payouts-table" name="shop-payouts" searchPlaceholder="Transfer reference…" />;
};

const ReviewsTab = ({ floristId }) => {
  const table = useTable("/admin/tables/reviews", { florist_id: floristId });
  const columns = [
    { key: "created_at", label: "Date", sortable: true, render: (r) => shortDate((r.created_at || "").slice(0, 10)) },
    { key: "rating", label: "Rating", sortable: true, render: (r) => <span className="tabular">{"★".repeat(r.rating)}</span> },
    { key: "body", label: "Review", render: (r) => <span className="text-xs">{r.title ? <b>{r.title} — </b> : null}{(r.body || "").slice(0, 140)}</span> },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
  ];
  return <DataTable table={table} columns={columns} testid="florist-reviews-table" name="shop-reviews" searchPlaceholder="Words in a review…" />;
};

const ActivityTab = ({ floristId }) => {
  const table = useTable("/admin/tables/activity", { entity_id: floristId });
  const columns = [
    { key: "created_at", label: "When", sortable: true, render: (r) => stamp(r.created_at) },
    { key: "actor_email", label: "Who" },
    { key: "action", label: "Action", render: (r) => <Badge tone="ink">{r.action}</Badge> },
    { key: "summary", label: "Detail", render: (r) => <span className="text-xs">{r.summary}</span> },
  ];
  return <DataTable table={table} columns={columns} testid="florist-activity-table" name="shop-activity" empty="No back-office action on this shop yet." />;
};

// Per-shop plan card — admins can (a) change the base plan, (b) set per-feature
// overrides that beat the plan for THIS florist only. All overrides live in
// `plan_overrides` on the florist doc and are wiped when the plan_id changes.
const PlanPanel = ({ floristId, onSaved }) => {
  const [state, setState] = useState(null);
  const [planId, setPlanId] = useState("");
  const [overrides, setOverrides] = useState({});
  const [applyNow, setApplyNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() =>
    api.get(`/admin/florists/${floristId}/plan`)
      .then((r) => {
        setState(r.data);
        setPlanId(r.data.florist.plan_id || "");
        setOverrides(r.data.florist.plan_overrides || {});
      })
      .catch((e) => setMsg(errText(e))), [floristId]);
  useEffect(() => { load(); }, [load]);
  if (!state) return <Panel title="Plan" testid="florist-plan-panel"><p className="text-xs text-muted">Loading plan…</p></Panel>;
  const eff = state.current;
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await api.put(`/admin/florists/${floristId}/plan`, {
        plan_id: planId || state.florist.plan_id,
        overrides,
        apply_immediately: applyNow,
      });
      setMsg(applyNow ? "Plan changé immédiatement." : "Plan changera le 1er du mois prochain.");
      await load();
      onSaved && onSaved();
    } catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };
  const featRow = (label, key, kind = "num") => (
    <div className="flex items-center gap-2" data-testid={`plan-override-${key}`}>
      <span className="text-xs text-muted w-52 shrink-0">{label}</span>
      {kind === "bool" ? (
        <select className="field text-xs" value={overrides[key] === undefined ? "" : String(overrides[key])}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = { ...overrides };
                  if (v === "") delete next[key]; else next[key] = v === "true";
                  setOverrides(next);
                }}>
          <option value="">plan par défaut ({String(eff.features[key])})</option>
          <option value="true">oui</option>
          <option value="false">non</option>
        </select>
      ) : kind === "select" ? (
        <select className="field text-xs" value={overrides[key] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = { ...overrides };
                  if (v === "") delete next[key]; else next[key] = v;
                  setOverrides(next);
                }}>
          <option value="">plan par défaut ({eff.features[key]})</option>
          {(kind === "select" && key === "payout_period" ? ["weekly", "biweekly", "monthly"] :
            key === "analytics_depth" ? ["basic", "advanced"] : []).map((o) =>
              <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type="number" className="field text-xs w-24"
               placeholder={String(eff.features[key])}
               value={overrides[key] ?? ""}
               onChange={(e) => {
                 const v = e.target.value;
                 const next = { ...overrides };
                 if (v === "") delete next[key]; else next[key] = parseInt(v, 10);
                 setOverrides(next);
               }} />
      )}
    </div>
  );
  return (
    <Panel title="Plan & privilèges" testid="florist-plan-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs text-muted mb-1">Plan de base</label>
          <select className="field text-sm" value={planId} data-testid="plan-id-select"
                  onChange={(e) => setPlanId(e.target.value)}>
            {state.all_plans.map((p) =>
              <option key={p.id} value={p.id}>{`${p.name} · ${p.commission_pct}% HT`}</option>)}
          </select>
          <div className="text-xs text-muted mt-2">
            Plan effectif : <b>{eff.name}</b> · commission {eff.commission_pct}% HT.
            {state.florist.plan_pending ? (
              <div className="mt-1 text-brass" data-testid="plan-pending-hint">
                Un changement vers <b>{state.florist.plan_pending.plan_id}</b> est prévu pour {state.florist.plan_pending.effective_at?.slice(0, 10)}.
              </div>
            ) : null}
          </div>
          <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer">
            <input type="checkbox" data-testid="plan-apply-now"
                   checked={applyNow} onChange={(e) => setApplyNow(e.target.checked)} />
            <span>Appliquer immédiatement (sinon 1<sup>er</sup> du mois prochain)</span>
          </label>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted">Surclassements par fleuriste (facultatif — laisse vide pour utiliser le plan) :</p>
          {featRow("Bouquets max", "product_limit", "num")}
          {featRow("Multi-utilisateurs", "multi_user", "bool")}
          {featRow("Multi-villes", "multi_city", "bool")}
          {featRow("Villes supplémentaires max", "max_cities", "num")}
          {featRow("Zones de livraison", "delivery_zones", "num")}
          {featRow("Fréquence versement", "payout_period", "select")}
          {featRow("Réseau Fleurinelle", "use_network_delivery", "bool")}
          {featRow("Bouquets en vedette", "featured_slots", "num")}
          {featRow("Profondeur d'analyse", "analytics_depth", "select")}
          {featRow("Accès API", "api_access", "bool")}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button className="btn-primary text-xs disabled:opacity-50"
                data-testid="plan-save" disabled={busy} onClick={save}>
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        {msg ? <span className="text-xs text-muted" data-testid="plan-save-msg">{msg}</span> : null}
      </div>
    </Panel>
  );
};

export default function FloristProfile() {
  const { floristId } = useParams();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "orders";
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const [commission, setCommission] = useState("");
  const [emailVal, setEmailVal] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/florists/${floristId}/profile`);
      setD(data);
      setCommission(String(data.florist.commission_percent));
      setEmailVal(data.florist.email || "");
    } catch (e) {
      setMsg(errText(e));
    }
  }, [floristId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, done) => {
    setMsg("");
    try {
      await fn();
      setMsg(done);
      await load();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  if (!d) return <p className="text-sm text-muted" data-testid="florist-profile-loading">{msg || "Loading the shop…"}</p>;
  const f = d.florist;
  const k = d.kpis;

  return (
    <div className="space-y-5" data-testid="florist-profile">
      <div className="flex flex-wrap items-start gap-4">
        {f.logo_url ? <img src={media(f.logo_url)} alt="" className="w-20 h-20 object-cover border border-brass-light" /> : null}
        <div>
          <h1 className="font-display text-3xl text-ink" data-testid="florist-profile-name">{f.shop_name}</h1>
          <p className="text-sm text-muted">{f.owner_name} · {f.city} · joined {shortDate((f.created_at || "").slice(0, 10))}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            <Badge tone={statusTone(f.status)} testid="florist-profile-status">{f.status}</Badge>
            {f.paused ? <Badge tone="warn">paused</Badge> : <Badge tone="good">taking orders</Badge>}
            {f.sale_percent ? <Badge tone="warn">shop sale −{f.sale_percent}%</Badge> : null}
            {f.verified_at ? <Badge tone="good">verified</Badge> : <Badge tone="warn">not verified</Badge>}
          </div>
        </div>
        <Link to="/admin/florists" className="btn-ghost text-xs ms-auto">← All shops</Link>
      </div>

      {msg ? <p className="text-sm text-brass" data-testid="florist-profile-message">{msg}</p> : null}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="florist-kpi-orders" label="Orders" value={k.orders} hint={`${k.delivered} delivered · ${k.live} live`} />
        <Stat testid="florist-kpi-revenue" label="Revenue" value={`${money(k.revenue_mad)} MAD`} />
        <Stat testid="florist-kpi-owed" label="Owed now" value={`${money(k.owed_mad)} MAD`} hint="delivered, not yet paid out" />
        <Stat testid="florist-kpi-fees" label="Card fees absorbed" value={`${money(k.card_fees_mad)} MAD`} />
        <Stat testid="florist-kpi-rating" label="Rating" value={k.review_average ?? f.rating ?? "—"} hint={`${k.reviews} reviews`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel title="Shop record" testid="florist-record">          <KeyRow label="Phone">{f.phone}</KeyRow>
          <KeyRow label="WhatsApp">{f.whatsapp}</KeyRow>
          <KeyRow label="Email">{f.email}</KeyRow>
          <KeyRow label="Address">{f.address}</KeyRow>
          <KeyRow label="Zones">{f.zones?.join(", ")}</KeyRow>
          <KeyRow label="Capacity">{f.capacity_per_day} a day</KeyRow>
          <KeyRow label="Acceptance">{Math.round((f.acceptance_rate || 0) * 100)}% · on time {Math.round((f.on_time_rate || 0) * 100)}%</KeyRow>
          <KeyRow label="Routing weight">{f.priority_weight}</KeyRow>
        </Panel>

        <Panel title="Bank & documents" testid="florist-bank">
          <KeyRow label="Bank">{f.bank?.bank_name}</KeyRow>
          <KeyRow label="Holder">{f.bank?.account_holder}</KeyRow>
          <KeyRow label="RIB">····{f.bank?.rib_last4 || "----"}</KeyRow>
          <KeyRow label="Application">{d.application ? `${d.application.stage} · ${shortDate((d.application.created_at || "").slice(0, 10))}` : "—"}</KeyRow>
          {f.bank?.pending_bank_change ? (
            <div className="mt-3 border border-warning/40 bg-warning/5 p-3 text-xs space-y-2" data-testid="florist-pending-bank-change">
              <p className="font-medium text-ink">Pending bank change — needs your review</p>
              <div className="tabular text-charcoal space-y-0.5">
                <p>Bank: <b>{f.bank.pending_bank_change.bank_name}</b></p>
                <p>Holder: <b>{f.bank.pending_bank_change.account_holder}</b></p>
                <p>New RIB: <b>····{f.bank.pending_bank_change.rib_last4}</b></p>
                <p className="text-muted">Requested {shortDate((f.bank.pending_bank_change.requested_at || "").slice(0, 10))}</p>
              </div>
              {f.bank.pending_bank_change.attestation_url ? (
                <a href={media(f.bank.pending_bank_change.attestation_url)} target="_blank" rel="noreferrer"
                   className="btn-ghost inline-flex items-center gap-1 text-xs py-1 px-2"
                   data-testid="florist-pending-bank-attestation">
                  View attestation
                </a>
              ) : null}
              <div className="flex gap-2 pt-1">
                <button className="btn-primary text-xs py-1.5 px-3" data-testid="florist-pending-bank-approve"
                        onClick={() => act(() => api.post(`/florist/admin/florists/${floristId}/bank-change`, { decision: "approve" }),
                                          "New RIB approved — florist notified.")}>Approve</button>
                <button className="btn-ghost text-xs py-1.5 px-3 text-danger" data-testid="florist-pending-bank-reject"
                        onClick={() => {
                          const reason = window.prompt("Why is the new RIB being rejected?\nThe florist gets this by e-mail with a re-upload link.",
                                                        "Attestation illisible — merci de recharger un document estampillé de la banque.");
                          if (reason === null) return;
                          act(() => api.post(`/florist/admin/florists/${floristId}/bank-change`, { decision: "reject", note: reason || "à recharger" }),
                              "New RIB rejected — florist notified.");
                        }}>Reject</button>
              </div>
              <p className="text-[11px] text-muted">Payouts keep flowing to the current RIB until approved.</p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {(d.documents || []).map((doc) => (
              <a key={doc.id} href={media(doc.url)} target="_blank" rel="noreferrer" className="border border-brass-light p-2 text-[11px]" data-testid={`florist-doc-${doc.id}`}>
                {doc.label}<span className={`block ${doc.status === "approved" ? "text-success" : doc.status === "rejected" ? "text-danger" : "text-muted"}`}>{doc.status}</span>
              </a>
            ))}
            {(d.documents || []).length === 0 ? <p className="text-sm text-muted">No document on file.</p> : null}
          </div>
        </Panel>

        <Panel title="Ops actions" testid="florist-actions">
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost text-[11px]" data-testid="florist-toggle-status"
                    onClick={() => act(() => api.patch(`/admin/florists/${f.id}`, { status: f.status === "active" ? "suspended" : "active", reason: "ops decision" }), f.status === "active" ? "Suspended." : "Reactivated.")}>
              {f.status === "active" ? "Suspend shop" : "Reactivate shop"}
            </button>
            <button className="btn-ghost text-[11px]" data-testid="florist-toggle-pause"
                    onClick={() => act(() => api.post(`/admin/florists/${f.id}/pause`, { paused: !f.paused, reason: "ops decision" }), f.paused ? "Taking orders again." : "Paused.")}>
              {f.paused ? "Resume orders" : "Pause orders"}
            </button>
            {f.sale_percent ? (
              <button className="btn-ghost text-[11px]" data-testid="florist-clear-sale"
                      onClick={() => act(() => api.post(`/admin/shop-sales/${f.id}/clear`), "Sale cleared.")}>Clear the sale</button>
            ) : null}
            <button className="btn-primary text-[11px]" data-testid="florist-impersonate"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/admin/florists/${f.id}/impersonate`);
                        // Stash the admin's own token so the florist app can restore it in one click.
                        const adminTok = localStorage.getItem("fl_token");
                        if (adminTok) localStorage.setItem("fl_admin_token", adminTok);
                        localStorage.setItem("fl_impersonating", JSON.stringify({
                          shop_name: data.florist.shop_name, florist_id: data.florist.id,
                        }));
                        localStorage.setItem("fl_token", data.token);
                        window.location.href = "/florist";
                      } catch (e) { alert(errText(e)); }
                    }}>
              Sign in as this shop →
            </button>
          </div>
          <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); act(() => api.patch(`/admin/florist-email`, { florist_id: f.id, email: emailVal }), "Login email updated."); }}>
            <label className="block space-y-1 flex-1">
              <span className="smallcaps">Login email</span>
              <input className="ctl w-full" value={emailVal} data-testid="florist-email-input" onChange={(e) => setEmailVal(e.target.value)} />
            </label>
            <button className="btn-ghost text-xs" data-testid="florist-email-save">Change</button>
          </form>
          <div className="text-sm space-y-1">
            {Object.entries(d.orders_by_status).map(([s, n]) => (
              <p key={s} className="flex justify-between border-b border-brass-light py-1">
                <Link className="underline decoration-brass underline-offset-2" to={`/admin/orders?florist_id=${f.id}&status=${s}`}>{s}</Link>
                <span className="tabular">{n}</span>
              </p>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <MultiCityPanel florist={f} onSaved={load} />
      </div>

      <TabBar testid="florist-tabs" value={tab} onChange={(t) => setSp({ tab: t }, { replace: true })}
              tabs={[["orders", "Orders", k.orders], ["catalogue", "Catalogue", k.products_published],
                     ["payouts", "Earnings & payouts"], ["reviews", "Reviews", k.reviews], ["activity", "Admin log"]]} />
      {tab === "orders" ? <OrdersTab floristId={floristId} /> : null}
      {tab === "catalogue" ? <CatalogueTab floristId={floristId} /> : null}
      {tab === "payouts" ? <PayoutsTab floristId={floristId} /> : null}
      {tab === "reviews" ? <ReviewsTab floristId={floristId} /> : null}
      {tab === "activity" ? <ActivityTab floristId={floristId} /> : null}
    </div>
  );
}
